import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type Profile = Database['public']['Tables']['profiles']['Row'];
type AppRole = Database['public']['Enums']['app_role'];

export interface ProfileComRole {
  id: string;
  user_id: string;
  nome: string | null;
  avatar_url?: string | null;
  criado_em: string;
  role: AppRole | null;
  ativo: boolean;
}

/**
 * Lists all profiles visible to the caller.
 * Relies on the `profiles` RLS policy for org scoping — this function
 * does not pass organization_id explicitly (matches legacy behavior).
 */
export async function listProfiles(): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('nome');

  if (error) throw error;
  return data ?? [];
}

/**
 * Lists org members joined to their profile rows, optionally filtered by role(s).
 * Two-step fetch: members for the org, then profiles for those user_ids.
 * Merges gracefully when a profile row is missing (role kept, other fields null).
 * By default only returns active members (ativo=true). Pass incluirInativos=true
 * to also include deactivated members (e.g. for the team management page).
 */
export async function listProfilesComRoles(params: {
  orgId: string;
  roles?: AppRole[];
  incluirInativos?: boolean;
}): Promise<ProfileComRole[]> {
  const { orgId, roles, incluirInativos = false } = params;

  let membersQuery = supabase
    .from('organization_members')
    .select('user_id, role, ativo')
    .eq('organization_id', orgId);

  if (!incluirInativos) {
    membersQuery = membersQuery.eq('ativo', true);
  }
  if (roles && roles.length > 0) {
    membersQuery = membersQuery.in('role', roles);
  }

  const { data: members, error: membersError } = await membersQuery;
  if (membersError) throw membersError;
  if (!members || members.length === 0) return [];

  const userIds = members.map((m) => m.user_id);
  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('id, user_id, nome, avatar_url, criado_em')
    .in('user_id', userIds)
    .order('nome');

  if (profilesError) throw profilesError;

  const memberByUser = new Map(members.map((m) => [m.user_id, m]));
  return (profiles ?? []).map<ProfileComRole>((profile) => {
    const m = memberByUser.get(profile.user_id);
    return {
      id: profile.id,
      user_id: profile.user_id,
      nome: profile.nome,
      avatar_url: profile.avatar_url,
      criado_em: profile.criado_em,
      role: (m?.role as AppRole) ?? null,
      ativo: m?.ativo ?? true,
    };
  });
}

/**
 * Fetches a single profile by user_id.
 * Returns null if no profile exists for that user (PGRST116 — "not found").
 * Throws on other Supabase errors (RLS denial, network, etc).
 */
export async function getProfileByUserId(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data;
}

const AVATAR_BUCKET = 'avatars';

/**
 * Faz upload da foto do utilizador para o bucket público `avatars`
 * (caminho `{userId}/avatar-{timestamp}.{ext}`) e devolve o URL público.
 * Remove os ficheiros antigos da pasta antes de gravar, para não acumular órfãos.
 */
export async function uploadAvatar(params: { userId: string; file: File }): Promise<string> {
  const ext = params.file.name.split('.').pop()?.toLowerCase() || 'png';
  const path = `${params.userId}/avatar-${Date.now()}.${ext}`;

  const { data: antigos } = await supabase.storage.from(AVATAR_BUCKET).list(params.userId);
  if (antigos && antigos.length > 0) {
    await supabase.storage
      .from(AVATAR_BUCKET)
      .remove(antigos.map((f) => `${params.userId}/${f.name}`));
  }

  const { error } = await supabase.storage
    .from(AVATAR_BUCKET)
    .upload(path, params.file, { upsert: true, contentType: params.file.type });
  if (error) throw error;

  const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Grava (ou limpa, com null) o avatar_url do utilizador autenticado,
 * via RPC security-definer que edita a própria linha.
 */
export async function setAvatarUrl(params: { avatarUrl: string | null }): Promise<void> {
  const { error } = await supabase.rpc('definir_avatar_perfil', { p_avatar_url: params.avatarUrl });
  if (error) throw error;
}

/**
 * Remove a foto: apaga o objeto do storage (se o path for derivável do URL)
 * e limpa o avatar_url.
 */
export async function removeAvatar(params: { userId: string; avatarUrl: string | null }): Promise<void> {
  if (params.avatarUrl) {
    const marker = `/${AVATAR_BUCKET}/`;
    const idx = params.avatarUrl.indexOf(marker);
    if (idx !== -1) {
      const path = params.avatarUrl.slice(idx + marker.length);
      const { error } = await supabase.storage.from(AVATAR_BUCKET).remove([path]);
      if (error) throw error;
    }
  }
  await setAvatarUrl({ avatarUrl: null });
}

/**
 * Atualiza os dados pessoais do utilizador autenticado (telefone + endereço),
 * via RPC security-definer que edita só a própria linha em profiles.
 * Valores vazios devem ser passados como null.
 */
export async function atualizarMeuPerfil(params: {
  telefone: string | null;
  morada: string | null;
  codigoPostal: string | null;
  cidade: string | null;
  pais: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('atualizar_meu_perfil', {
    p_telefone: params.telefone,
    p_morada: params.morada,
    p_codigo_postal: params.codigoPostal,
    p_cidade: params.cidade,
    p_pais: params.pais,
  });
  if (error) throw error;
}
